import { categoryOnAdd } from "../../api/src/shared/grocery/index.js";
import type { Anchors, Item } from "../lib/api.js";
import { el } from "../lib/dom.js";
import { icon } from "../lib/icons.js";
import { actionRow, banner, CONFIRM_INERT_MS, messageOf, offersDue, offersQuantity, type Popup, type ScreenHost } from "./ui.js";
import { itemFields } from "./itemForm.js";
import type { WriteGate } from "./listPolicy.js";

/**
 * The Add item popup: what the plus opens on a named list, and what Insert
 * below in a row's Details opens with a position already chosen.
 *
 * It replaced the inline composer at the top of the list (I01). That form was
 * always on screen, took the caret from the floating Add by scrolling to
 * itself, and pointed at a row through a line of small print above it. This
 * is one surface, opened on purpose, that says where the item is going and
 * closes when it has gone.
 *
 * Nothing here waits on the network. `store.createItem` returns the row before
 * anything is sent, so the popup closes on the local write and the saved/synced
 * distinction stays the shell's to show.
 */

/**
 * Where the item goes, as the pair of neighbours the server takes.
 *
 * Both travel, captured when the position is chosen and never re-read at
 * submit — see `Anchors` in `lib/api.ts`. A caller preselecting a position
 * (Insert below, and g07's Insert above) passes the pair it captured; the
 * selector shows it as "After <row>" and lets the person change their mind.
 */
export interface AddPosition {
  afterId?: string | null;
  beforeId?: string | null;
}

/** Exported so the tests pin the words, as `EMPTY_ITEM` is. */
export const LIST_GONE =
  "This list was deleted, so the item can't be added here. Copy the text if you want to keep it.";
export const ANCHOR_GONE =
  "That item is no longer in this list. Position is back to End — choose again if you want somewhere else.";

/** The selector's values: the two ends, and every row by id. */
const START = "start";
const END = "end";
const after = (id: string): string => `after:${id}`;

const DISCARD = {
  title: "Discard this item?",
  message: "What you typed will be lost.",
  keep: "Keep editing",
  discard: "Discard",
};

export function openAddItem(
  shell: ScreenHost,
  listId: string,
  position?: AddPosition,
  options: {
    /** Where the focus goes when the popup closes: the plus, or the row's Details. */
    returnFocusTo?: HTMLElement;
    /** After each successful write, with the row the store made. The list reveals it. */
    onAdded?(item: Item): void;
    /**
     * Where the Add passes on a shared list (g72): a link's editor with no
     * name is asked for one as a step of this popup, and the Add goes through
     * once they have given it, with everything typed still here.
     */
    gate?: WriteGate;
  } = {},
): Popup | undefined {
  const { store } = shell;
  const list = store.list(listId);
  if (!list) return undefined;

  /**
   * The pair that goes to the server, and the moment it is set.
   *
   * Computed when the position is *chosen* — in the selector's `change`, or
   * from what the caller captured — and never at submit. Reading the list
   * again at submit looked equivalent and was not: the neighbour can be gone
   * by then, and a fresh lookup would quietly append. Held, the pair either
   * still means what it meant or is replaced on purpose (see `onItems`).
   *
   * End is the empty pair, not "after the last row": naming the last row on
   * this device would put the item *before* anything another device appended
   * meanwhile. Beginning has no such sentinel and names the first row.
   */
  let anchors: Anchors = {
    afterId: position?.afterId ?? null,
    beforeId: position?.beforeId ?? null,
  };

  // Set once the list is gone. From then on there is nothing to protect and
  // nothing to submit: the text stays on screen to be copied, and Cancel is
  // the way out rather than a question about a list that no longer exists.
  let gone = false;

  const destinationCaption = el("p", { class: "popup-note" });
  const paintDestinationCaption = (name: string): void => {
    destinationCaption.textContent = `Adding to ${name}`;
  };
  paintDestinationCaption(list.name);
  const fields = itemFields("add", {}, shell.announce);
  const { item: itemField, date: dateField } = fields;
  const dueFields = el("div", { class: "recurrence-fields", attrs: { hidden: !offersDue(list.kind) }, children: [
    fields.rows.due,
    fields.rows.repeat,
  ] });
  const quantityRow = fields.rows.quantity;
  quantityRow.hidden = !offersQuantity(list.kind);
  // A category is offered on every kind of list (g81), so its row is never hidden.

  /**
   * On a Shopping list the Category field follows the Item text (g82): it
   * shows the aisle the item will be filed under, and changes as the text
   * does. The first `input` in the field itself, whether typing, clearing or
   * picking a suggestion, makes it the person's, and the form leaves it alone
   * from then on. Setting `.value` fires no event, so the form's own fill
   * never counts as theirs.
   *
   * A field the form filled is not a choice, so a kind that has no aisles
   * takes the value away again, and a row that is not shown is never filled.
   */
  let categoryIsTheirs = false;
  const followText = (): void => {
    if (categoryIsTheirs || gone || fields.rows.category.hidden) return;
    fields.category.value = categoryOnAdd(store.list(listId)?.kind ?? "", itemField.value.trim(), null) ?? "";
  };
  itemField.addEventListener("input", followText);
  fields.category.addEventListener("input", () => { categoryIsTheirs = true; });

  /**
   * Note and the schedule, folded away until they are wanted (g54). A plain
   * reminder is the Item and Add; a note or a due date is one tap further.
   *
   * A native disclosure: closed, its fields are out of the tab order and the
   * accessibility tree with nothing to keep in step, and its summary is a
   * button with its state. Closed, it still holds its values — they are in
   * the draft and in the write — so its summary says what it holds, and what
   * needs fixing, in the words the row will use: nothing folded away is
   * saved unseen. Opening and closing it changes no value and no draft; it
   * starts closed each time Add opens and stays as the person leaves it.
   */
  const heldLine = el("span", { class: "more-details-held" });
  const problemLine = el("span", { class: "more-details-problem" });
  const moreDetails = el("details", {
    class: "more-details",
    children: [
      el("summary", {
        class: "more-details-summary",
        children: [el("span", { class: "more-details-label", text: "More details" }), heldLine, problemLine, icon("chevron")],
      }),
      el("div", { class: "form-group more-details-body", children: [fields.rows.note, dueFields] }),
    ],
  });
  // Each part is read after the label as part of the summary's name, so a
  // comma a screen reader hears stands where the eye sees the gap.
  const paintPart = (line: HTMLElement, parts: string[]): void => {
    line.replaceChildren(...(parts.length ? [el("span", { class: "visually-hidden", text: ", " }), parts.join(" · ")] : []));
  };
  const paintHeld = (): void => {
    const { values, problems } = fields.held();
    // Open, the fields speak for themselves; a problem is said either way.
    paintPart(heldLine, moreDetails.open ? [] : values);
    paintPart(problemLine, problems);
  };
  for (const type of ["input", "change", "toggle"]) moreDetails.addEventListener(type, paintHeld);

  /** A field inside a closed disclosure cannot take the focus: open it first. */
  const focusField = (control: HTMLElement): void => {
    if (moreDetails.contains(control)) moreDetails.open = true;
    control.focus();
  };

  /**
   * Position: Beginning, after any row — ticked ones included, because nothing
   * sinks and "after Bread" means exactly that — and End (I03). A native
   * `<select>`, so a phone gets its own picker and a keyboard gets type-ahead
   * for free, and one row's option text is its accessible name in full.
   */
  const positionField = el("select", {
    attrs: { id: "add-position", "aria-describedby": "add-position-error" },
  });
  const positionError = el("span", {
    class: "field-error",
    attrs: { id: "add-position-error", hidden: true },
  });
  const positionRow = el("div", {
    class: "field",
    attrs: { id: "add-position-chooser", hidden: true },
    children: [
      el("label", { class: "field-label", text: "Position", attrs: { for: "add-position" } }),
      positionField,
      positionError,
    ],
  });

  /**
   * The position as one line, with a way to change it (g54). Most items go at
   * the end and need no chooser competing with the Item field; one opened
   * from Insert above or below says where it is going in words. Change swaps
   * the line for the selector, which then stays for as long as the popup
   * does: taking it away again would hide a choice just made.
   */
  const positionSummary = el("p", { class: "popup-note position-summary", attrs: { id: "add-position-summary" } });
  const changePosition = el("button", {
    class: "secondary position-change",
    text: "Change",
    attrs: {
      type: "button",
      "aria-label": "Change position",
      "aria-expanded": "false",
      "aria-controls": "add-position-chooser",
      "aria-describedby": "add-position-summary",
    },
    on: { click: () => { revealChooser(); positionField.focus(); } },
  });
  const positionLine = el("div", { class: "position-line", children: [positionSummary, changePosition] });
  const paintSummary = (): void => {
    positionSummary.textContent = `Position: ${positionField.selectedOptions[0]?.textContent ?? "End"}`;
  };
  const revealChooser = (): void => {
    positionRow.hidden = false;
    positionLine.hidden = true;
    changePosition.setAttribute("aria-expanded", "true");
  };

  /** What the caller's pair means as a choice: a row, the start, or the end. */
  const initialChoice = (): string => {
    if (anchors.afterId) return after(anchors.afterId);
    if (anchors.beforeId) return START;
    return END;
  };

  /**
   * Rebuilt from the store, keeping the choice. Every row is an option, so a
   * rename or an arrival on another device has to reach here — and the
   * selector is a control somebody may be looking at, so it is repainted in
   * place rather than replaced.
   *
   * Says whether the choice survived. A `<select>` handed a value it has no
   * option for shows nothing chosen and reads as "" — and the pair in hand
   * would still name the row that is gone. Either caller takes the answer to
   * `anchorGone`, because a position nobody can see must not be submitted.
   */
  const paintPositions = (keep: string): boolean => {
    const rows = store.items(listId);
    positionField.replaceChildren(
      el("option", { text: "Beginning", attrs: { value: START } }),
      ...rows.map((row) => el("option", { text: `After ${row.text}`, attrs: { value: after(row.id) } })),
      el("option", { text: "End", attrs: { value: END } }),
    );
    positionField.value = keep;
    const kept = positionField.value === keep;
    if (!kept) positionField.value = END;
    paintSummary();
    return kept;
  };
  // The row Insert below chose can be gone before this opens: undocked, the
  // panel comes down through `history.back()`, and the popup waits for that.
  const openedWithAnchor = paintPositions(initialChoice());
  let initialPosition = positionField.value;

  const clearPositionError = (): void => {
    positionError.textContent = "";
    positionError.hidden = true;
    positionField.removeAttribute("aria-invalid");
  };

  /** The moment the pair is set — see `anchors`. */
  const choosePosition = (): void => {
    const rows = store.items(listId);
    const choice = positionField.value;
    if (choice === START) {
      anchors = { afterId: null, beforeId: rows[0]?.id ?? null };
    } else if (choice === END) {
      anchors = { afterId: null, beforeId: null };
    } else {
      const id = choice.slice(after("").length);
      const at = rows.findIndex((row) => row.id === id);
      anchors = { afterId: id, beforeId: rows[at + 1]?.id ?? null };
    }
    clearPositionError();
    paintSummary();
  };
  positionField.addEventListener("change", choosePosition);

  /**
   * The chosen row is gone (I06). The draft is the thing worth keeping, so
   * the text stays and the *position* is what is taken back: End, marked,
   * explained beside the selector and said aloud, with the focus on the
   * selector so the next thing typed is a choice rather than more text going
   * somewhere it no longer can.
   */
  const anchorGone = (): void => {
    // A choice that had to be taken back is shown as the selector, never as
    // a summary line: the person is asked to choose again.
    revealChooser();
    anchors = { afterId: null, beforeId: null };
    positionField.value = END;
    paintSummary();
    initialPosition = END;
    positionError.textContent = ANCHOR_GONE;
    positionError.hidden = false;
    positionField.setAttribute("aria-invalid", "true");
    shell.announce(ANCHOR_GONE);
    positionField.focus();
  };

  const status = el("div", { class: "popup-status" });
  const addButton = el("button", { text: "Add", attrs: { type: "submit" } });
  /**
   * Deliberate repeated entry (I05). Saves the same way, then clears only
   * what was about the item — Item, Note and Quantity — and keeps the caret here.
   * At an anchored position the next one goes *after the one just added*,
   * not after the original row again, which would file a list typed in order
   * in reverse. End stays End, and needs nothing done.
   */
  const againButton = el("button", {
    class: "secondary",
    text: "Add another",
    attrs: { type: "button" },
    on: { click: () => save(true) },
  });
  const cancelButton = el("button", {
    class: "secondary",
    text: "Cancel",
    attrs: { type: "button" },
    on: { click: () => popup.dismiss() },
  });

  // When the last write took. See the top of `save`.
  let savedAt = -Infinity;

  /** One write path for Add and Add another; `again` is the only difference. */
  function save(again: boolean): void {
    if (gone) return;
    // The second half of a double tap on Add another, or a held Enter, lands
    // on the form it has just emptied. It is not a request to add nothing, so
    // it is not refused: it does nothing, beyond taking the focus back to
    // Item from the button it pressed. A blank submit a moment later is.
    if (!fields.item.value.trim() && !fields.unfinished() && performance.now() - savedAt < CONFIRM_INERT_MS) {
      fields.item.focus();
      return;
    }

    const invalid = fields.validate();
    if (invalid) {
      paintHeld();
      focusField(invalid);
      return;
    }
    const values = fields.read();

    const { gate } = options;
    if (gate && !gate.ready()) {
      gate.hold({
        within: popup,
        // Read again when it runs: the step kept every field as it was.
        run: () => save(again),
        valid: () => !gone && store.list(listId) !== undefined,
        returnFocusTo: () => addButton,
      });
      return;
    }

    let row: Item;
    try {
      // The row appears because the store says it exists, not because this
      // handler adds it. Nothing to disable and nothing to await.
      // A blank Category the person left themselves, or one in a row they
      // cannot see, stays blank. Otherwise the store fills as the field did.
      row = store.createItem(listId, values, anchors, { keepBlankCategory: categoryIsTheirs || fields.rows.category.hidden === true });
    } catch (error: unknown) {
      // Only a value the store will not queue reaches here — there is no
      // request to fail. Everything typed stays where it is (I04).
      const message = messageOf(error);
      status.replaceChildren(banner(message));
      shell.announce(message);
      return;
    }

    savedAt = performance.now();
    status.replaceChildren();
    options.onAdded?.(row);
    if (!again) {
      popup.close();
      return;
    }

    fields.reset();
    // The next item starts fresh: its category follows its text again.
    categoryIsTheirs = false;
    paintHeld();
    if (positionField.value !== END) {
      // The store told `onItems` synchronously, so the new row is already an
      // option; choosing it computes the pair the same way a person's choice
      // would.
      positionField.value = after(row.id);
      choosePosition();
    }
    initialPosition = positionField.value;
    itemField.focus();
  }

  const form = el("form", {
    class: "add-item item-form",
    attrs: { novalidate: true },
    children: [
      // Where the item is going, first: the list, and the place in it.
      el("div", { class: "popup-destination", children: [destinationCaption, positionLine, positionRow] }),
      // What the item is, then everything that can wait.
      el("div", { class: "form-group", children: [fields.rows.item, quantityRow, fields.rows.category] }),
      moreDetails,
      status,
      actionRow([cancelButton, againButton], addButton),
    ],
    on: {
      submit: (event) => {
        event.preventDefault();
        save(false);
      },
    },
  });

  // The popup is not rebuilt for a delta — it is a form somebody is filling
  // in — so it is told what changed instead.
  const stopItems = store.onItems(listId, () => {
    if (!paintPositions(positionField.value)) anchorGone();
  });
  const stopLists = store.onLists((change) => {
    if (change.id !== listId) return;
    if (!change.list) {
      gone = true;
      addButton.disabled = true;
      againButton.disabled = true;
      status.replaceChildren(el("p", { class: "muted", text: LIST_GONE }));
      shell.announce(LIST_GONE);
      return;
    }
    paintDestinationCaption(change.list.name);
    // The kind decides what is offered, and a field that already holds
    // something stays: hidden, it would still be saved, and nobody could see
    // or correct it (D05).
    quantityRow.hidden = !offersQuantity(change.list.kind) && !fields.quantity.value;
    dueFields.hidden = !offersDue(change.list.kind) && !dateField.value && !fields.unfinished();
    // Only a Shopping list has aisles: a category the form put there goes or
    // comes back with the kind, and one the person typed stays.
    followText();
  });

  // Anything in a field, a date or time only partly typed included, or a
  // position chosen: every one of them is something the person would lose.
  const typed = (): boolean => fields.typed() || positionField.value !== initialPosition;

  const popup = shell.openPopup({
    title: "Add item",
    body: form,
    focus: itemField,
    returnFocusTo: options.returnFocusTo,
    navigationDirty: () => typed(),
    guard: {
      dirty: () => !gone && typed(),
      ...DISCARD,
    },
    onClose: () => {
      stopItems();
      stopLists();
    },
  });

  // After the host has put the focus on Item, so the selector wins it (I06:
  // "request a new position").
  if (!openedWithAnchor) anchorGone();

  return popup;
}
